const CONSENT_MAPPING_BY_ID =
  require("@zalando-internal/zalando-consent-api").CONSENT_MAPPING_BY_ID;

// caching the consents promise to only fetch them once per page load
let pendingConsents;

// used to patch the consent map as App and Web use different serviceIds
// for the same use case, besides that apps use the names as identifier and
// web uses the serviceId
export const getConsentMap = (isAppRequest) => {
  if (isAppRequest) {
    return {
      ...CONSENT_MAPPING_BY_ID,
      observability_sdk_zalando: { name: "Lightstep for Zalando" },
      observability_sdk_lounge: {
        name: "Lightstep for Zalando Lounge",
      },
    };
  }

  return CONSENT_MAPPING_BY_ID;
};

export const getConsent = async function (id) {
  const url = new URL(window.location.href);
  const clientCategory = url.searchParams.get("client_category"); // can be fs or lounge

  let serviceId = id;

  // map specific consent in case we have an app request
  if (clientCategory === "lounge") {
    switch (serviceId) {
      // both observability_sdk_zalando and Lightstep for Zalando are mapped to the same serviceId at Lounge
      case "observability_sdk_zalando": {
        serviceId = "observability_sdk_lounge";
        break;
      }
      case "Lightstep for Zalando": {
        serviceId = "Lightstep for Zalando Lounge";
        break;
      }
      case "ga_zalando": {
        serviceId = "ga_lounge";
        break;
      }
      case "google_ads_zalando": {
        serviceId = "google_ads_lounge";
        break;
      }
      default:
    }
  }

  const consentMap = getConsentMap(isAppRequest(url));
  const service = consentMap[serviceId];

  // use a list in case we need to add more domains in the future
  if (["customer-iam-test.zalandoapis.com"].includes(url.hostname)) {
    return {
      status: true,
      serviceId,
      name: service !== undefined ? service.name : "unknown",
    };
  }

  if (pendingConsents === undefined) {
    pendingConsents = fetchConsents(url);
  }

  const consents = await pendingConsents;

  const consent = consents.find((consent) => {
    // for apps we might not find the right service and fallback comparing the consent name with the serviceId, see Lightstep above
    return consent.name === service.name;
  });
  let consentStatus = false;
  if (consent !== undefined) {
    consentStatus = !!consent.status;
  }
  return {
    status: consentStatus,
    serviceId,
    name: service,
  };
};

export const subscribe = function () {
  return () => false;
};

export const activateScripts = function () {
  return Promise.resolve();
};

/**
 * Parse the `tc` query param looking for `dcid:` or `zcid:`.
 *   1) If `dcid:` is found, it becomes deviceConsentId => mobile-app flow.
 *   2) Otherwise, if `zcid:` is found, it becomes zalandoClientId => browser flow.
 *   3) If neither is found, we fallback to normal device_consent_id or zalando_client_id.
 *   4) If none is found at all, return null.
 */
function parseConsentIdentifiers(url) {
  const tc = url.searchParams.get("tc");

  let deviceConsentId = null;
  let zalandoClientId = null;

  if (tc) {
    // decode once in case it's URL-encoded
    const decoded = decodeURIComponent(tc);
    const parts = decoded.split(",");

    // We'll do a single pass, but our "priority" is:
    //  - if we find `dcid:` anywhere, pick that
    //  - if we do not find `dcid`, but do find `zcid:`, pick that
    // so we store if we see either, but only set device first if we see it.
    let foundDcid = null;
    let foundZcid = null;

    for (const part of parts) {
      const trimmed = part.trim(); // e.g. "dcid:c36c7db3-b011" or "zcid:b48709e0"
      if (trimmed.toLowerCase().startsWith("dcid:")) {
        foundDcid = trimmed.substring("dcid:".length);
      } else if (trimmed.toLowerCase().startsWith("zcid:")) {
        foundZcid = trimmed.substring("zcid:".length);
      }
    }

    if (foundDcid) {
      deviceConsentId = foundDcid;
    } else if (foundZcid) {
      zalandoClientId = foundZcid;
    }
  }

  // If we still don't have either, let's fallback to direct query params
  if (!deviceConsentId && !zalandoClientId) {
    deviceConsentId = url.searchParams.get("device_consent_id");
    zalandoClientId = url.searchParams.get("zalando_client_id");
  }

  // If after that, neither is found => null (we should not fetch consent from opt-out service)
  if (!deviceConsentId && !zalandoClientId) {
    return null;
  }

  return { deviceConsentId, zalandoClientId };
}

export const isAppRequest = (url) => {
  const identifiers = parseConsentIdentifiers(url);

  if (!identifiers) {
    return false;
  }

  return !!identifiers.deviceConsentId;
};

async function fetchConsents(url) {
  try {
    const identifiers = parseConsentIdentifiers(url);

    if (!identifiers) {
      return [];
    }

    const { deviceConsentId, zalandoClientId } = identifiers;
    const isAppRequest = !!deviceConsentId;

    const headers = {
      "Content-Type": "application/json",
      "x-frontend-type": isAppRequest ? "mobile-app" : "browser",
    };

    let pathname = `/api/sso/consents`;

    if (isAppRequest) {
      // for mobile-app requests, the opt-out service requires device_consent_id as query param
      pathname += `?device_consent_id=${deviceConsentId}`;
    } else {
      headers["x-zalando-client-id"] = zalandoClientId;
    }

    const response = await fetch(pathname, {
      method: "GET",
      headers,
    });

    /**
     * Example shape of response data:
     * {"consents":[{"status":true,"name":"Client Metrics Aggregator for Zalando","updated_at":"0"}]}
     */
    const data = await response.json();
    const consents = data.consents || [];
    return consents;
  } catch (error) {
    console.warn("Error fetching consent", error);
    return [];
  }
}

// only needed for testing purposes
export function __resetPendingConsents() {
  pendingConsents = undefined;
}
